<template>
  <div style="min-height: calc(100vh - 176px)" class="flex flex-wrap mt-4">
    <div class="relative py-2 px-2 flex flex-col w-full shadow-lg rounded-lg bg-blueGray-100 border-0">
      <div class="bg-white shadow-md p-4 rounded-lg flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          v-model="searchQuery"
          @input="updateFilters"
          placeholder="Поиск по заголовку..."
          class="border p-2 rounded w-full sm:w-1/3"
        />
        <select
          v-model="sortField"
          @change="updateFilters"
          class="border px-3 py-2 pr-7 rounded whitespace-nowrap"
        >
          <option value="title">По заголовку {{ sortOrder === 'asc' ? '↑' : '↓' }}</option>
        </select>
        <button
          @click="toggleSortOrder"
          class="border px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 whitespace-nowrap"
        >
          {{ sortOrder === 'asc' ? '↑ Возрастание' : '↓ Убывание' }}
        </button>
        <select
          v-model="selectedStatus"
          @change="updateFilters"
          class="border p-2 rounded w-full sm:w-1/4"
        >
          <option value="">Все статусы</option>
          <option value="true">Подтвержден</option>
          <option value="false">Не подтвержден</option>
        </select>
        <select
          v-model="selectedArchived"
          @change="updateFilters"
          class="border p-2 rounded w-full sm:w-1/4"
        >
          <option value="">Все</option>
          <option value="false">Активные</option>
          <option value="true">В архиве</option>
        </select>
        <router-link
          :to="{ path: '/admin/course-preparation-add' }"
          v-slot="{ href, navigate }"
        >
          <button
            :href="href"
            @click="navigate"
            class="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
            type="button"
          >
            <i style="margin-right: 15px" class="fas fa-plus"></i>
            Добавить
          </button>
        </router-link>
      </div>

      <div class="bg-white shadow-md rounded-lg overflow-hidden">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-200">
              <th class="p-3">#</th>
              <th class="p-3">Заголовок</th>
              <th class="p-3">Преподаватель</th>
              <th class="p-3">Описание</th>
              <th class="p-3">Цена</th>
              <th class="p-3">Кол-во часов</th>
              <th class="p-3">Статус</th>
              <th class="p-3">Архив</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in courses" :key="item.id" class="border-t">
              <td class="p-3">{{ index + 1 + (currentPage - 1) * perPage }}</td>
              <td class="p-3">
                <router-link
                  :to="{ name: 'CoursePreparationSettings', params: { id: item.id } }"
                  class="text-blue-600 hover:underline"
                >
                  {{ item.title }}
                </router-link>
              </td>
              <td class="p-3">{{ item.teacher }}</td>
              <td class="p-3">{{ item.description }}</td>
              <td class="p-3">{{ item.price }}</td>
              <td class="p-3">{{ item.hours }}</td>
              <td class="p-3">
                <template v-if="user.role.key === 0 || user.role.key === 3">
                  <button
                    @click="toggleConfirm(item)"
                    :class="item.is_confirmed ? 'bg-gray-500' : 'bg-green-400'"
                    class="text-white f-14 px-3 py-1 rounded"
                  >
                    {{ item.is_confirmed ? "Отменить" : "Подтвердить" }}
                  </button>
                </template>
                <template v-else>
                  {{ item.is_confirmed ? "Подтвержден" : "Не подтвержден" }}
                </template>
              </td>
              <td class="p-3">
                <template v-if="user.role.key === 0 || user.role.key === 3">
                  <button
                    @click="toggleArchive(item)"
                    :class="item.is_archived ? 'bg-blue-500' : 'bg-red-500'"
                    class="text-white f-14 px-3 py-1 rounded"
                  >
                    {{ item.is_archived ? "Вернуть" : "Архивировать" }}
                  </button>
                </template>
                <template v-else>
                  {{ item.is_archived ? "В архиве" : "Активный" }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-center mt-4">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-gray-300 rounded mr-2"
        >
          ← Назад
        </button>
        <span class="px-4 py-2"
          >Страница {{ currentPage }} из {{ totalPages }}</span
        >
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage >= totalPages"
          class="px-4 py-2 bg-gray-300 rounded ml-2"
        >
          Вперед →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_COURSE_PREPARATION, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from "@/const/uri";
import { mapState } from "vuex";

export default {
  data() {
    return {
      courses: [],
      searchQuery: "",
      selectedStatus: "",
      selectedArchived: "",
      currentPage: 1,
      perPage: DEFAULT_PER_PAGE,
      totalPages: 1,
      sortField: "title",
      sortOrder: "asc",
    };
  },
  computed: mapState(['user']),
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.fetchCourses();
      },
    },
  },
  methods: {
    async fetchCourses() {
      const query = this.$route.query;

      this.searchQuery = query.search || "";
      this.selectedStatus = query.is_confirmed || "";
      this.selectedArchived = query.is_archived || "";
      this.currentPage = Number(query.page) || 1;
      this.sortField = query.sort || "title";
      this.sortOrder = query.order || "asc";

      const orderingField = this.sortOrder === 'desc' ? `-${this.sortField}` : this.sortField;

      await axios({
        method: "GET",
        url: API_COURSE_PREPARATION.list,
        params: {
          search: this.searchQuery,
          is_confirmed: this.selectedStatus,
          is_archived: this.selectedArchived,
          p: this.currentPage,
          page_size: this.perPage,
          ordering: orderingField,
        },
        headers: HEADERS_TOKEN_JSON(),
      })
        .then((e) => {
          const res = e.data.data;
          this.courses = res.results;
          this.totalPages = Math.ceil(res.count / this.perPage);
        })
        .catch((err) => {
          console.error("Ошибка загрузки курсов:", err);
        });
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.updateFilters();
    },

    async toggleConfirm(item) {
      const url = `${API_COURSE_PREPARATION.list}${item.id}/${
        item.is_confirmed ? "unconfirm" : "confirm"
      }/`;
      await axios.post(
        url,
        {},
        {
          headers: HEADERS_TOKEN_JSON(),
        }
      );
      this.fetchCourses();
    },

    async toggleArchive(item) {
      const url = `${API_COURSE_PREPARATION.list}${item.id}/${
        item.is_archived ? "unarchive" : "archive"
      }/`;
      await axios.post(
        url,
        {},
        {
          headers: HEADERS_TOKEN_JSON(),
        }
      );
      this.fetchCourses();
    },

    updateFilters() {
      this.$router.push({
        query: {
          search: this.searchQuery || undefined,
          is_confirmed: this.selectedStatus || undefined,
          is_archived: this.selectedArchived || undefined,
          sort: this.sortField,
          order: this.sortOrder,
          page: 1,
        },
      });
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$router.push({ query: { ...this.$route.query, page } });
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("ru-RU", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
};
</script>
